import { el, svg } from "../core/dom";
import { styles } from "../styles/modal";
import type { Settings, GuardianVerificationErrorCode } from "../api/types";
import type { ResolvedColors } from "./colors";

export type VerificationScreenRefs = {
  container: HTMLElement;
};

export function getVerificationErrorMessage(
  errorCode: string | null,
  fallbackError: string | null
): string {
  switch (errorCode) {
    case "GUARDIAN_UNDER_18":
      return "The guardian must be 18 years or older. Please provide details of a different guardian.";
    case "TOKEN_FAILED":
      return "DigiLocker verification could not be completed. Please try again.";
    case "DIGILOCKER_AUTH_FAILED":
      return "DigiLocker authorization was cancelled or failed. Please try again.";
    case "NO_AUTH_CODE":
      return "DigiLocker did not return an authorization code. Please try again.";
    case "SESSION_EXPIRED":
      return "Verification session has expired. Please try again.";
    default:
      return fallbackError || "Verification failed. Please try again.";
  }
}

export function renderVerificationScreen(options: {
  settings?: Partial<Settings>;
  colors: ResolvedColors;
  isMobile: boolean;
  logoUrl: string;
  isInitiating: boolean;
  isPolling: boolean;
  isComplete: boolean;
  isAutoTransitioning?: boolean;
  error: string | null;
  errorCode: string | null;
  canRetry: boolean;
  onContinue: () => void;
  onBack: (clearName?: boolean) => void;
}): VerificationScreenRefs {
  const {
    settings, colors, isMobile, logoUrl,
    isInitiating, isPolling, isComplete, isAutoTransitioning,
    error, errorCode, canRetry,
    onContinue, onBack,
  } = options;

  const headingColor = settings?.headingColor || "#101828";
  const textColor = colors.text;
  const font = colors.font;
  const accentColor = colors.acceptBg;

  // ─── Top Section ───
  const logo = el("img", { src: logoUrl, style: styles.logo, alt: "Logo" });
  const title = el("h2", {
    id: "verification-title",
    style: {
      ...styles.title,
      fontSize: "16px",
      fontWeight: 600,
      color: headingColor,
      fontFamily: font,
    },
  });
  title.textContent = "Guardian Verification";

  const topLeft = el("div", { style: styles.topLeft }, logo, title);
  const topSection = el("div", {
    style: { ...styles.topSection, color: textColor, fontFamily: font },
  }, topLeft);

  // ─── Middle Section ───
  const middleSection = el("div", {
    style: {
      ...styles.middleSection,
      color: textColor,
      fontFamily: font,
      textAlign: "center",
    },
  });

  if (isComplete && !error) {
    // Success state: green checkmark circle
    const successCircle = el("div", {
      style: {
        width: "64px",
        height: "64px",
        borderRadius: "50%",
        backgroundColor: "#D1FAE5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    });
    const checkSvg = svg(
      "svg",
      {
        width: "32", height: "32", viewBox: "0 0 24 24",
        fill: "none", stroke: "#059669", "stroke-width": "2",
        "stroke-linecap": "round", "stroke-linejoin": "round",
      },
      svg("polyline", { points: "20 6 9 17 4 12" })
    );
    successCircle.appendChild(checkSvg);

    const successText = el("p", {
      style: {
        color: headingColor,
        fontFamily: font,
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "1.5",
        marginTop: "12px",
        marginBottom: "24px",
      },
    });
    successText.textContent = "Verification Completed";

    const wrapper = el("div", {
      style: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: "1",
      },
    }, successCircle, successText);
    middleSection.appendChild(wrapper);

  } else if ((isInitiating || isPolling) && !error) {
    // Polling state: spinner + status message
    const spinner = el("div", {
      style: {
        width: "48px",
        height: "48px",
        border: "4px solid #e5e7eb",
        borderTopColor: accentColor,
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        margin: "24px auto",
      },
    });

    const statusMsg = el("div", {
      style: {
        color: "#059669",
        fontSize: "14px",
        marginTop: "16px",
        padding: "12px",
        borderRadius: "6px",
        border: "1px solid #A7F3D0",
        backgroundColor: "#D1FAE5",
        textAlign: "center",
      },
    });
    statusMsg.textContent = isInitiating
      ? "Opening DigiLocker in a new tab..."
      : "Please complete the verification in the DigiLocker tab. This page will update automatically when verification is complete.";

    middleSection.appendChild(spinner);
    middleSection.appendChild(statusMsg);
  }

  if (error) {
    // Error state: red X circle + error text
    const errorCircle = el("div", {
      style: {
        width: "64px",
        height: "64px",
        borderRadius: "50%",
        backgroundColor: "#fee2e2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "24px auto",
      },
    });
    const xSvg = svg(
      "svg",
      {
        width: "32", height: "32", viewBox: "0 0 24 24",
        fill: "none", stroke: "#dc2626", "stroke-width": "2",
        "stroke-linecap": "round", "stroke-linejoin": "round",
      },
      svg("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
      svg("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
    );
    errorCircle.appendChild(xSvg);

    const failedText = el("p", {
      style: {
        ...styles.privacyText,
        color: textColor,
        fontFamily: font,
        fontSize: "14px",
        lineHeight: "1.5",
        marginBottom: "12px",
      },
    });
    failedText.textContent = "Verification failed";

    const errorBox = el("div", {
      style: {
        color: "#DC2626",
        fontSize: "14px",
        marginTop: "16px",
        padding: "12px",
        borderRadius: "6px",
        border: "1px solid #FCA5A5",
        backgroundColor: "#FEF2F2",
        textAlign: "center",
      },
    });
    errorBox.textContent = error;

    middleSection.appendChild(errorCircle);
    middleSection.appendChild(failedText);
    middleSection.appendChild(errorBox);
  }

  // ─── Bottom Section ───
  const btnBase = {
    ...styles.button,
    fontSize: isMobile ? "14px" : "16px",
    padding: isMobile ? "10px 20px" : "9px 45px",
  };

  const bottomSection = el("div", {
    style: {
      ...styles.bottomSection,
      paddingTop: "12px",
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      color: textColor,
      fontFamily: font,
    },
  });

  const acceptBtnStyle = {
    backgroundColor: colors.acceptBg,
    color: colors.acceptText,
    borderRadius: colors.borderRadius,
  };
  const declineBtnStyle = {
    backgroundColor: colors.declineBg,
    color: colors.declineText,
    borderColor: colors.declineBorder,
    borderRadius: colors.borderRadius,
  };

  // Hide bottom section during auto-transition
  if (!isAutoTransitioning) {
    if (isComplete && !error) {
      const continueBtn = el("button", {
        style: { ...btnBase, ...acceptBtnStyle },
        type: "button",
        "aria-label": "Continue to consent form",
        onClick: onContinue,
      });
      continueBtn.textContent = "Continue";
      bottomSection.appendChild(continueBtn);
    } else if (error && canRetry) {
      const retryBtn = el("button", {
        style: { ...btnBase, ...styles.acceptButton, ...acceptBtnStyle },
        type: "button",
        "aria-label": errorCode === "GUARDIAN_UNDER_18" ? "Change guardian details" : "Try verification again",
        onClick: () => onBack(errorCode === "GUARDIAN_UNDER_18"),
      });
      retryBtn.textContent = errorCode === "GUARDIAN_UNDER_18" ? "Change Guardian" : "Try Again";
      bottomSection.appendChild(retryBtn);
    } else if (error) {
      const goBackBtn = el("button", {
        style: { ...btnBase, ...styles.cancelButton, ...declineBtnStyle },
        type: "button",
        "aria-label": "Go back",
        onClick: () => onBack(false),
      });
      goBackBtn.textContent = "Go Back";
      bottomSection.appendChild(goBackBtn);
    } else {
      // Polling state — show cancel
      const cancelBtn = el("button", {
        style: { ...btnBase, ...styles.cancelButton, ...declineBtnStyle },
        type: "button",
        "aria-label": "Cancel verification",
        onClick: () => onBack(false),
      });
      cancelBtn.textContent = "Cancel";
      bottomSection.appendChild(cancelBtn);
    }
  }

  // ─── Assemble ───
  const container = el("div", { style: styles.content }, topSection, middleSection, bottomSection);

  return { container };
}
